import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Heart } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";

export default function About() {
  return (
    <div className="min-h-screen overflow-x-clip bg-crave-bg font-sans text-crave-cream">
      <Seo
        title="About Foodie Crave | Why We Built Discover & Dine"
        description="The story behind Foodie Crave: Discover & Dine — a dining experience app with restaurant discovery built in, created by a husband-and-wife duo who love eating out."
        path="/about"
      />
      <Navbar />
      <main>
        <section className="relative overflow-hidden pt-32 pb-24 md:pt-44 md:pb-32" data-testid="about-page">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-48 -left-48 h-[36rem] w-[36rem] rounded-full bg-crave-red/25 blur-[140px]"
          />
          <div className="relative mx-auto max-w-3xl px-6 md:px-12">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-crave-gold">About</p>
              <h1 className="mt-4 font-display text-4xl font-medium leading-[1.05] tracking-tight text-crave-cream sm:text-5xl md:text-6xl">
                Why we created Foodie Crave
              </h1>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-10 space-y-6 text-base leading-relaxed text-crave-muted md:text-lg">
                <p>
                  Foodie Crave started with a simple idea: finding new places to eat is fun, but
                  keeping track of everywhere you want to try — and everywhere you've enjoyed —
                  shouldn't be complicated.
                </p>
                <p>
                  We wanted a simple way to discover restaurants, save favorites, remember our
                  visits, and plan an occasional outing with family or friends.
                </p>
                <p>So we started building Foodie Crave.</p>
                <p>
                  Our goal is to make dining out a little easier to explore, enjoy, and remember —
                  without turning the experience into another social media feed.
                </p>
                <p>
                  In practice, that means a dining experience app with restaurant discovery built
                  in: explore restaurants near home or at your next destination, save the places
                  you want to try, keep track of every visit, and remember the meals worth
                  repeating. Invitations and group coordination are there when you want company —
                  and never required when you don't.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.18}>
              <div className="mt-14 rounded-3xl border border-crave-border bg-crave-card p-8 md:p-10">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-crave-gold/30 bg-crave-gold/10 text-crave-gold">
                  <Heart className="h-5 w-5" aria-hidden="true" />
                </span>
                <h2 className="mt-5 font-display text-2xl font-medium tracking-tight text-crave-cream sm:text-3xl">
                  From our kitchen table
                </h2>
                <p className="mt-3 text-base leading-relaxed text-crave-muted">
                  Foodie Crave is built by <span className="font-semibold text-crave-cream">Knight Rose</span> —
                  a husband-and-wife duo who wanted an app to keep tabs on our own saved places and
                  visits, without the complications and the social pressure. We're building the app
                  we always wanted for ourselves, and sharing it with everyone who feels the same.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.24}>
              <div className="mt-8 rounded-3xl border border-crave-border bg-crave-card p-8 md:p-10">
                <h2 className="font-display text-2xl font-medium tracking-tight text-crave-cream sm:text-3xl">
                  Where we are today
                </h2>
                <p className="mt-3 text-base leading-relaxed text-crave-muted">
                  Foodie Crave is in pre-launch early access. Android testing is underway through
                  Google Play's testing program, and iOS is coming soon — you can join the waiting
                  list to be notified. Everything on this site reflects what we're building toward,
                  and early testers help shape what launches.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="mt-12 flex flex-wrap items-center gap-4">
                <Link
                  to="/"
                  data-testid="about-home-link"
                  className="inline-flex items-center gap-2.5 rounded-full bg-crave-gold px-7 py-3.5 text-sm font-bold text-crave-bg transition-colors duration-300 hover:bg-crave-cream"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  Back to home
                </Link>
                <a
                  href="mailto:support@foodiecrave.app"
                  data-testid="about-contact-link"
                  className="inline-flex items-center gap-2.5 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-crave-cream transition-colors duration-300 hover:border-crave-gold hover:text-crave-gold"
                >
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  support@foodiecrave.app
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
